/**
 * Engine: winning-path derivation.
 *
 * Winning is no longer a persisted per-node flag. The persisted shape carries
 * `winningRecordIds` (ordered ancestor entityRecordId chain). The UI derives
 * the highlighted key set at render time via `computeWinningKeySet`; the
 * mark-winning handler computes the new chain via `computeWinningRecordIds`.
 * Ancestors whose `entityRecordId` is undefined are omitted from the persisted
 * chain — they re-resolve at read time by treating undefined-record nodes as
 * transparent links between matched records. `hasWinningCombination` is the
 * trivial non-empty test; `hasWinningChildren` takes the precomputed key set
 * so callers do not re-walk the tree per row. `hasCompletePathWithRecords`
 * gates the mark-winning menu item on full ancestor record selection. Legacy
 * per-node `isWinningCombination` flags on already-persisted data are stripped
 * by `utils/migrateRelationshipTreeNodes.ts`. All exports are pure.
 */
import type { RelationshipTreeNode } from '../System.RelationshipTree.types';

const findPathToNode = (
  trees: RelationshipTreeNode[],
  targetKey: string,
): RelationshipTreeNode[] => {
  const path: RelationshipTreeNode[] = [];
  const walk = (
    nodes: RelationshipTreeNode[],
    currentPath: RelationshipTreeNode[],
  ): boolean => {
    for (const node of nodes) {
      const newPath = [...currentPath, node];
      if (node.key === targetKey) {
        path.push(...newPath);
        return true;
      }
      if (node.children && walk(node.children, newPath)) return true;
    }
    return false;
  };
  walk(trees, []);
  return path;
};

/**
 * Returns the ordered `entityRecordId` chain from root to the node identified
 * by `nodeKey`, skipping ancestors whose `entityRecordId` is undefined. This
 * is the canonical persisted form of "winning" (`winningRecordIds`). Returns
 * `[]` when the key is not in the tree.
 */
export const computeWinningRecordIds = (
  trees: RelationshipTreeNode[],
  nodeKey: string,
): string[] => {
  const path = findPathToNode(trees, nodeKey);
  if (path.length === 0) return [];
  return path
    .map((node) => node.entityRecordId)
    .filter((id): id is string => typeof id === 'string' && id.length > 0);
};

/**
 * Returns the set of node `key`s on the winning path. Resolves the persisted
 * `winningRecordIds` chain back to concrete tree nodes by walking the tree
 * looking for a contiguous parent→child chain whose successive record ids
 * match the input in order. Two relaxations make the resolution robust to
 * tree restructuring:
 *
 * 1. The matched chain can start at any depth (not just at the top-level
 *    nodes). Nodes above the match are NOT added to the returned set.
 * 2. Once a match has started, ancestors with no `entityRecordId` are
 *    transparent — the walk skips through them and they are added to the set
 *    so the highlight covers the full visual path.
 *
 * When no full match exists, returns an empty set (never partial flags).
 * Empty `winningRecordIds` also returns an empty set.
 *
 * Because winning is derived from `winningRecordIds` at render time and the
 * field is controlled via `value`/`onChange`, the legacy SYD-1843 scenario
 * (duplicate `isWinningCombination` flags surviving load) and SYD-1857
 * (unmark lost after a Firestore merge round-trip on persisted node data)
 * are architecturally unreachable: no per-node flag exists to duplicate or
 * to outlive a merge. Pre-existing flags on persisted documents are stripped
 * by `utils/migrateRelationshipTreeNodes.ts`.
 */
export const computeWinningKeySet = (
  trees: RelationshipTreeNode[],
  winningRecordIds: string[],
): Set<string> => {
  if (winningRecordIds.length === 0) return new Set();
  const matchedKeys = new Set<string>();
  const findMatch = (
    nodes: RelationshipTreeNode[],
    index: number,
  ): boolean => {
    for (const node of nodes) {
      if (node.entityRecordId === winningRecordIds[index]) {
        if (index === winningRecordIds.length - 1) {
          matchedKeys.add(node.key);
          return true;
        }
        if (node.children && node.children.length > 0 && findMatch(node.children, index + 1)) {
          matchedKeys.add(node.key);
          return true;
        }
      } else if (index === 0) {
        if (node.children && node.children.length > 0 && findMatch(node.children, 0)) {
          return true;
        }
      } else if (
        node.entityRecordId === undefined &&
        node.children &&
        node.children.length > 0 &&
        findMatch(node.children, index)
      ) {
        matchedKeys.add(node.key);
        return true;
      }
    }
    return false;
  };
  findMatch(trees, 0);
  return matchedKeys;
};

/**
 * Trivial non-empty test on the persisted summary. The React layer uses this
 * to lock peer rows once any winning chain is set.
 */
export const hasWinningCombination = (
  winningRecordIds: string[] | undefined,
): boolean => (winningRecordIds?.length ?? 0) > 0;

/**
 * True when any descendant key of `node` is on the winning path. Takes the
 * precomputed `winningKeySet` (from `computeWinningKeySet`) so the per-row
 * walk does not re-resolve the chain.
 */
export const hasWinningChildren = (
  node: RelationshipTreeNode,
  winningKeySet: Set<string>,
): boolean => {
  if (!node.children || node.children.length === 0) return false;
  return node.children.some(
    (child) =>
      winningKeySet.has(child.key) || hasWinningChildren(child, winningKeySet),
  );
};

/**
 * Returns whether `nodeKey` is reachable along an ancestor chain in which
 * every node carries an `entityRecordId`. Used to gate the "Mark winning"
 * menu item when `requiresCompletePathForWinning` is set on the entity
 * config. Does NOT assert that the path's `entityOrGroupId` sequence matches
 * the configured hierarchy — that is enforced upstream by `allowedChildren`
 * at the time nodes are added.
 */
export const hasCompletePathWithRecords = (
  trees: RelationshipTreeNode[],
  nodeKey: string,
): boolean => {
  const path = findPathToNode(trees, nodeKey);
  if (path.length === 0) return false;
  return path.every((node) => !!node.entityRecordId);
};
